import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import * as _ from 'lodash';

import { utils } from '@blend/ui-common';

import * as textScreenAPI from 'login-apis/textScreenAPI';
import {
  colors,
  fonts,
  FullScreenModal,
  fullScreenModalHeaderHeight,
  Heading,
  IconButton,
  Markup,
  media,
  mixins,
  mobileHeaderHeight,
  mobileUtils,
  RawLinkButton,
  StickyFooter,
  TitleCard,
} from '@blend/consumer-components';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import { getOrganizationColors } from 'login-utils/deploymentConfig';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import TextWithLinks from './TextWithLinks';
import {
  AcceptedTermsTypes,
  getSeparatedDisplayPartsFromAcceptedTermsTypes,
  getTermsTypesDisplayInfo,
  TermsTypesDisplayInfo,
} from './utils';

const { domUtils } = utils;

export const modalAnimationWaitMs = 500;
const organizationColors = getOrganizationColors();
const cardTopMargin = 40;
const cardBottomMargin = 58;
const contentCardWidth = 570;

const styles = StyleSheet.create({
  link: {
    display: 'block',
    color: colors.darkerGray,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  textScreenScroller: {
    padding: 24,
    overflowY: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    [media.mediumView]: {
      width: contentCardWidth,
    },
  },
  cardWrapper: {
    [media.mediumView]: {
      marginTop: cardTopMargin,
      marginBottom: cardBottomMargin,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    [media.mediumView]: {
      height: `calc(100vh - ${fullScreenModalHeaderHeight}px - ${cardTopMargin + cardBottomMargin}px)`,
    },
    [media.smallView]: {
      height: `calc(100vh - ${mobileHeaderHeight}px)`,
    },
  },
  conditionsText: {
    ...fonts.Roboto,
  },
  loading: {
    marginTop: 24,
    textAlign: 'center',
  },
  footerWrapper: {
    boxShadow: mixins.boxShadow(0, -4, 4, undefined, colors.snowPlus1),
  },
  modalButton: {
    [media.mediumView]: {
      ...mixins.paddingHorizontal(48),
      ...mixins.paddingVertical(24),
      width: contentCardWidth,
    },
    [media.smallView]: {
      ...mixins.padding(16),
      background: colors.white,
    },
  },
  mobileCloseButton: {
    marginLeft: 'auto',
  },
  tableOfContents: {
    marginTop: 32,
  },
  tableOfContentsItem: {
    fontWeight: fonts.weight.bold,
    fontSize: 14,
  },
  errorWrapper: {
    paddingTop: 16,
  },
});

namespace RequiredViewingRequirementInput {
  export interface OwnProps {
    acceptedTermsTypes: AcceptedTermsTypes[];
    submitButtonDisabled: boolean;
    submitError: string | null;
    submitForm: () => void;
  }

  export interface StateProps {
    userHasViewedTermsAndConditionsText: boolean | undefined;
  }

  export interface DispatchProps {
    setUserHasViewedTermsAndConditionsText: () => void;
  }

  export type Props = OwnProps & StateProps & DispatchProps;

  interface TextScreenContentObject extends TermsTypesDisplayInfo {
    content: string;
  }

  export interface State {
    modalOpen: boolean;
    selectedLink: string;
    textScreenContents: TextScreenContentObject[] | null;
  }
}

// This input is built for a very specific usage case, where the user is required to actually view the terms and
// conditions before accepting them. It's partially built off of the same logic as RequirementInput.

// However, it also introduces some design quirks, i.e. this input renders a button inside a
// modal that can submit the form. It's currently only built to work as a standalone input; there will be unexpected
// results if it's used alongside other inputs.
class RequiredViewingRequirementInput extends React.PureComponent<
  RequiredViewingRequirementInput.Props,
  RequiredViewingRequirementInput.State
> {
  constructor(props: RequiredViewingRequirementInput.Props) {
    super(props);
    this.state = {
      modalOpen: false,
      selectedLink: '',
      textScreenContents: null,
    };
  }

  openModal = (selectedLink = '') => {
    this.setState({
      modalOpen: true,
      selectedLink,
    });
  };

  dismissModal = () => {
    const { setUserHasViewedTermsAndConditionsText } = this.props;
    const { textScreenContents } = this.state;
    // if the conditionsText has not loaded, the user has not viewed the terms, so we don't count the modal as having
    // been opened. This state is in redux so that we can double-check it on submit and preserve it after un/remounts.
    if (!!textScreenContents) {
      setUserHasViewedTermsAndConditionsText();
    }
    this.setState({
      modalOpen: false,
      selectedLink: '',
    });
  };

  scrollConditionIntoView(textScreenId: string) {
    domUtils.scrollElementIntoScrollerView(
      document.getElementById(textScreenId)!,
      document.getElementById('textScreenContent-scroller')!,
      16,
      'auto',
    );
  }

  async componentDidMount() {
    // Kick off fetching of text screens and store them in textScreenContents
    const { acceptedTermsTypes } = this.props;
    const textScreenConfigs = _.compact(_.map(acceptedTermsTypes, condition => getTermsTypesDisplayInfo()[condition]));
    const textScreenContents = await Promise.all(
      _.map(textScreenConfigs, async ({ textScreenId, ...configProps }) => {
        const content = await textScreenAPI.getTextScreen(textScreenId);
        return { content, textScreenId, ...configProps };
      }),
    );

    this.setState({ textScreenContents });
  }

  renderConditionsText = () => {
    const { textScreenContents } = this.state;
    if (!textScreenContents) {
      return (
        <div className={css(styles.loading)}>
          <LoadingSpinner />
        </div>
      );
    }
    return (
      <div className={css(styles.conditionsText)}>
        <Heading text={'By clicking "Agree and Proceed" I agree to:'} fontSize={20} />
        <div className={css(styles.tableOfContents)} data-it="RequiredViewingRequirementInput-tableOfContents">
          {textScreenContents.map(({ textScreenId, linkDisplay }, index) => {
            return (
              <div key={`${textScreenId}-link`}>
                {/* Using RawLinkButton so that we can add a custom 'display: block' style for Edge tab highlights */}
                <RawLinkButton className={css(styles.link)} onClick={() => this.scrollConditionIntoView(textScreenId)}>
                  <span className={css(styles.tableOfContentsItem)}>{`${index + 1}. ${linkDisplay}`}</span>
                </RawLinkButton>
              </div>
            );
          })}
        </div>
        {textScreenContents.map(({ textScreenId, content }) => (
          <div className="TextViewer-htmlText" id={textScreenId} key={textScreenId}>
            <br />
            <Markup content={content} />
          </div>
        ))}
      </div>
    );
  };

  renderConditionsOverviewText = () => {
    const { acceptedTermsTypes, userHasViewedTermsAndConditionsText } = this.props;
    const acceptText = userHasViewedTermsAndConditionsText
      ? 'I confirm that I have read and agree to the '
      : 'Read and agree to the ';
    const linksDisplay = getSeparatedDisplayPartsFromAcceptedTermsTypes(acceptedTermsTypes, this.openModal);
    const textParts = [acceptText, ...linksDisplay, '.'];
    return (
      <div data-it="RequiredViewingRequirementInput-overviewText">
        <TextWithLinks textParts={textParts} />
      </div>
    );
  };

  renderOpenModalButton = () => (
    <PrimaryButton
      data-it="RequiredViewingRequirementInput-openModalButton"
      isWide
      text="Review"
      fontFamily="Roboto"
      type="button"
      onClick={_event => this.openModal()}
    />
  );

  renderSubmitButton = () => {
    const { submitForm, submitButtonDisabled } = this.props;
    return (
      <React.Fragment>
        <PrimaryButton
          data-it="RequiredViewingRequirementInput-submitButton"
          isWide
          text="Agree and Proceed"
          fontFamily="Roboto"
          type="submit"
          disabled={submitButtonDisabled}
          onClick={submitForm}
        />
      </React.Fragment>
    );
  };

  renderSubmitError = submitError =>
    submitError && (
      <div className={css(styles.errorWrapper)}>
        <ErrorMessage errorText={submitError} />
      </div>
    );

  render() {
    const { submitError, userHasViewedTermsAndConditionsText } = this.props;
    const { textScreenContents, modalOpen, selectedLink } = this.state;

    const TitleCardIfDesktop = mobileUtils.isMobile() ? React.Fragment : TitleCard;
    const StickyFooterIfMobile = mobileUtils.isMobile() ? StickyFooter : React.Fragment;

    const MobileCloseButton = (
      <div className={css(styles.mobileCloseButton)}>
        <IconButton
          data-it="DesktopEditModal-closeButton"
          actionColor={organizationColors.action}
          alignIcon="right"
          color="action"
          icon="delete"
          iconSize="lg"
          onClick={this.dismissModal}
        />
      </div>
    );

    return (
      <React.Fragment>
        {this.renderConditionsOverviewText()}
        <div className={css(paneFormStyles.buttonDisplay, paneFormStyles.buttonPadding)}>
          {userHasViewedTermsAndConditionsText ? this.renderSubmitButton() : this.renderOpenModalButton()}
        </div>
        {this.renderSubmitError(submitError)}
        {modalOpen && (
          <FullScreenModal
            data-it="RequiredViewingRequirementInput-termAndConditions"
            headerText={'Terms & Conditions'}
            closeText={''}
            contentLabel="Terms and Conditions"
            isOpen={modalOpen}
            handleClose={this.dismissModal}
            closeButtonColor={organizationColors.action}
            headerRightControl={MobileCloseButton}
            headerLeftControl={<React.Fragment />}
            headerFont="Roboto"
            onAfterOpen={() => {
              // set a timeout of modalAnimationWaitMs because if we do it before the modal entrance animation finishes,
              // the bounding rects get messed up and the scrollbar will go to the wrong place in the document :(
              // the value for modalAnimationWaitMs derives from the SCSS styling of the modal in consumer-components
              setTimeout(() => {
                if (selectedLink) {
                  this.scrollConditionIntoView(selectedLink);
                }
              }, modalAnimationWaitMs);
            }}
          >
            <div className={css(styles.cardWrapper)}>
              <TitleCardIfDesktop>
                <div className={css(styles.content)}>
                  <div className={css(styles.textScreenScroller)} id="textScreenContent-scroller">
                    {this.renderConditionsText()}
                  </div>
                  {textScreenContents && (
                    <div className={css(styles.footerWrapper)}>
                      <StickyFooterIfMobile>
                        <div className={css(styles.modalButton)}>
                          {this.renderSubmitButton()}
                          {this.renderSubmitError(submitError)}
                        </div>
                      </StickyFooterIfMobile>
                    </div>
                  )}
                </div>
              </TitleCardIfDesktop>
            </div>
          </FullScreenModal>
        )}
      </React.Fragment>
    );
  }
}

export const RequiredViewingRequirementInputConfig = {
  component: RequiredViewingRequirementInput,
};

export default RequiredViewingRequirementInput;
